import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Stethoscope,
  Brain,
  ShieldCheck,
  Calendar,
  Clock,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  PhoneCall,
  UserCheck,
} from "lucide-react";

export default function HowToSeeAPsychiatrist() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/how-to-see-a-psychiatrist.jpg";

  const faqs = [
    {
      q: "What is the primary difference between a psychiatrist and a psychologist?",
      a: "Psychiatrists are medical doctors (MD or DO) who can prescribe medication and manage physical/medical causes of psychiatric conditions. Psychologists hold doctoral degrees (PhD or PsyD) in psychology and focus on therapy, counseling, and psychological assessments.",
    },
    {
      q: "Do I need a referral to see a psychiatrist?",
      a: "This depends on your health insurance plan. HMO plans typically require a referral from a primary care provider, while PPO plans and self-pay options often allow direct scheduling.",
    },
    {
      q: "What should I bring to my first psychiatry appointment?",
      a: "Bring a photo ID, insurance card, list of current medications (including dosages), past medical/psychiatric records, and a written list of symptoms or questions you want to cover.",
    },
    {
      q: "Can a psychiatrist provide psychotherapy as well as medications?",
      a: "Yes, psychiatrists are trained in psychotherapy, though many work in tandem with licensed psychotherapists or clinical social workers to provide comprehensive integrated care.",
    },
    {
      q: "How long does a typical psychiatric evaluation take?",
      a: "An initial psychiatric intake evaluation usually lasts between 45 and 60 minutes, allowing thorough discussion of medical history, symptoms, lifestyle factors, and treatment goals.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How to See a Psychiatrist: A Step-by-Step Guide | Neuro Wellness"
        description="Learn how to find, prepare for, and visit a psychiatrist. Discover differences between psychiatrists and psychologists, when to seek help, and what to expect at your intake."
        canonical="/how-to-see-a-psychiatrist"
        keywords="how to see a psychiatrist, psychiatric evaluation, find a psychiatrist Florida, psychiatrist vs psychologist, mental health evaluation Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-to-see-a-psychiatrist#webpage",
                name: "How to See a Psychiatrist: A Complete Step-by-Step Guide",
                url: "https://neurowellnesstms.com/how-to-see-a-psychiatrist",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Understanding the role of a psychiatrist, identifying mental health symptoms, finding the right doctor, and preparing for your initial intake consultation.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-to-see-a-psychiatrist#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Stethoscope className="h-4 w-4" />
                    Psychiatric Care Guide
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How to See a Psychiatrist:{" "}
                  <span className="text-accent">Step-by-Step Guide</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Seeing a psychiatrist can be a transformative step toward
                  mental clarity and balance. Learn what psychiatrists do, when
                  to seek care, how to prepare, and how psychiatric evaluation
                  complements therapy.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <UserCheck className="h-4 w-4 text-accent" />
                    <span>Board-Certified Physicians</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Brain className="h-4 w-4 text-accent" />
                    <span>Comprehensive Diagnostics</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      Confidential Assessment
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Request a Consultation
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Speak with our psychiatric and clinical team in Coral
                      Springs, FL to start your evaluation.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="how-to-see-a-psychiatrist"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "How to See a Psychiatrist",
                href: "/how-to-see-a-psychiatrist",
              },
            ]}
          />
        </div>

        {/* CLINICAL DIRECTOR QUOTE BLOCK */}
        <section className="container-px mx-auto max-w-4xl py-8">
          <div className="rounded-2xl border border-accent/20 bg-accent/5 p-6 sm:p-8 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-accent/15 p-3 text-accent shrink-0">
                <Stethoscope className="h-6 w-6" />
              </div>
              <div>
                <blockquote className="text-base sm:text-lg italic text-foreground leading-relaxed">
                  “It’s important to note that while a psychiatrist, who
                  specializes in medicine, can and many times is instrumental in
                  the mental health recovery process, it does not replace the
                  reparative benefits of psychotherapy, also known as talk
                  therapy, that can include evidence-based interventions, and
                  the more long-term human connection of understanding,
                  compassionate communication, and empathic processing during
                  therapy.”
                </blockquote>
                <p className="mt-3 text-xs sm:text-sm font-semibold text-accent">
                  — Roxana Carmenate, LCSW-QS, Clinical Director & Lead
                  Therapist at Neuro Wellness TMS Centers of America
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Understanding the Role of a Psychiatrist
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Seeing a psychiatrist can be a highly beneficial step toward
              improving your mental health and overall life quality. However,
              it’s key to understand what a psychiatrist does and what to expect
              from your appointments before your initial visit.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <Brain className="h-5 w-5 text-accent" /> Psychiatrist (MD /
                  DO)
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Medical doctors who complete 4 years of medical school
                  followed by 4 years of psychiatric residency. They assess
                  physical biological factors, order lab work, perform medical
                  diagnostics, and prescribe psychiatric medications.
                </p>
              </div>
              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <UserCheck className="h-5 w-5 text-primary" /> Psychologist &
                  Therapist
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Doctoral-level psychologists or licensed therapists (LCSW,
                  LMHC) who specialize in psychotherapeutic modalities,
                  behavioral analysis, cognitive restructuring, and emotional
                  processing techniques.
                </p>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <h3 className="text-xl font-bold text-foreground">
                What to Expect During Your Evaluation
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                When you see a psychiatrist, expect a comprehensive review of
                your mental health history. This includes reviewing current
                symptoms, personal/family medical history, past medications,
                sleep habits, and lifestyle factors. The goal is to formulate an
                accurate clinical diagnosis and create an individualized
                treatment roadmap.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Identifying the Need for Psychiatric Care
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Recognizing when you need psychiatric support is an essential
              first step. Mental health conditions manifest through emotional,
              cognitive, physical, and behavioral signs:
            </p>

            <ul className="grid sm:grid-cols-2 gap-3 pt-2 text-sm text-foreground">
              {[
                "Persistent sadness, emptiness, or crying spells",
                "Severe panic attacks or relentless worry",
                "Changes in appetite or dramatic weight shifts",
                "Chronic insomnia or excessive sleeping",
                "Inability to concentrate or mental fog",
                "Increased irritability or sudden emotional outbursts",
                "Loss of interest in hobbies and social withdrawal",
                "Thoughts of self-harm or feelings of worthlessness",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 bg-muted/30 p-3 rounded-lg border"
                >
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Finding & Preparing for Your Appointment
            </h2>
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-foreground">
                1. Researching Qualified Specialists
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Look for licensed, board-certified psychiatrists with strong
                clinical backgrounds. Ask your primary care physician for
                trusted referrals, consult local psychiatric centers in South
                Florida, or review specialty focus areas such as
                treatment-resistant depression or interventional psychiatry.
              </p>

              <h3 className="text-xl font-bold text-foreground">
                2. What to Bring to Your First Session
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                To maximize your intake appointment, come prepared with medical
                records, past psychiatric evaluations, insurance details, and a
                full list of current supplements and prescription drugs.
              </p>

              <h3 className="text-xl font-bold text-foreground">
                3. Key Questions to Ask Your Psychiatrist
              </h3>
              <div className="bg-muted/40 p-5 rounded-xl border space-y-2 text-sm">
                <p className="font-semibold text-foreground">
                  Recommended questions:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
                  <li>
                    What is my preliminary diagnosis and how was it determined?
                  </li>
                  <li>
                    What are the treatment options, including non-medication and
                    advanced therapies?
                  </li>
                  <li>
                    What are the potential side effects of any prescribed
                    medications?
                  </li>
                  <li>
                    How will therapy and medication management be integrated?
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Related Resources & Next Steps
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/how-to-get-mental-health-help"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    How To Get Mental Health Help
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Navigating available mental health resources
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
              <Link
                to="/services/medication-management"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    Medication Management
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Psychiatric evaluation and ongoing prescription care
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              Ready to Speak With a Medical Professional?
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              At Neuro Wellness TMS Centers of America in Coral Springs, FL, our
              multidisciplinary clinical team provides thorough psychiatric
              intake, medication management, psychotherapy, and advanced
              interventional care including SAINT® TMS and SPRAVATO®.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
